import { useState, useEffect } from 'react';
import { Settings, Copy, Trash2, ArrowRightLeft, Loader2, FolderKanban } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PROJECT_ICONS, PROJECT_COLORS } from '../sidebar/constants';
import api from '../../lib/api';
import { toast } from 'sonner';

const ProjectSettingsDialog = ({
  open,
  onOpenChange,
  projectId,
  projectName,
  setProjectName,
  projectColor,
  setProjectColor,
  projectIcon,
  setProjectIcon,
  onSave,
  onDelete,
  onDuplicate,
  onMoveComplete,
  loading
}) => {
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [movingProject, setMovingProject] = useState(false);

  // Fetch available workspaces when dialog opens
  useEffect(() => {
    if (open && projectId) {
      fetchAvailableWorkspaces();
    }
  }, [open, projectId]);

  const fetchAvailableWorkspaces = async () => {
    if (!projectId) {
      console.warn('fetchAvailableWorkspaces: projectId is undefined');
      setAvailableWorkspaces([]);
      return;
    }
    
    setLoadingWorkspaces(true);
    try {
      const response = await api.get(`/projects/${projectId}/available-workspaces`);
      setAvailableWorkspaces(response.data.workspaces || []);
    } catch (error) {
      console.error('Failed to fetch available workspaces:', error.response?.data || error.message);
      // If 403, user doesn't have permission to move this project
      if (error.response?.status === 403) {
        setAvailableWorkspaces([]);
      } else {
        setAvailableWorkspaces([]);
      }
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const handleMoveProject = async () => {
    if (!selectedWorkspace) {
      toast.error('Please select a target workspace');
      return;
    }
    if (!projectId) {
      toast.error('Project ID is missing. Please close and reopen the dialog.');
      console.error('Move project failed: projectId is undefined');
      return;
    }
    
    setMovingProject(true);
    try {
      console.log('Calling move API for project:', projectId, 'to workspace:', selectedWorkspace);
      const response = await api.post(`/projects/${projectId}/move`, {
        target_workspace_id: selectedWorkspace
      });
      console.log('Move API success:', response.data);
      
      // Close dialog and clear state FIRST before showing toast
      setSelectedWorkspace('');
      onOpenChange(false);
      
      // Show success toast
      toast.success(response.data.message || 'Project moved successfully');
      
      // Call onMoveComplete in a separate try-catch to avoid showing error toast
      try {
        onMoveComplete?.();
      } catch (callbackError) {
        console.error('onMoveComplete callback error:', callbackError);
        // Don't show error toast for callback errors - the move was successful
      }
    } catch (error) {
      console.error('Move project API error:', error.response?.data || error.message);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = errorDetail || 'Move failed. Please check console for details.';
      toast.error(errorMessage);
    } finally {
      setMovingProject(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md dark:bg-slate-800 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-gray-100">
            <Settings className="w-5 h-5 text-[#4A9CC7]" />
            Project Settings
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-project-name" className="dark:text-gray-300">Project Name</Label>
            <Input
              id="edit-project-name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Project name"
              data-testid="edit-project-name-input"
              className="dark:bg-slate-900 dark:border-slate-600"
            />
          </div>
          <div className="space-y-2">
            <Label className="dark:text-gray-300">Icon</Label>
            <div className="flex gap-2 flex-wrap">
              {PROJECT_ICONS.map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setProjectIcon(id)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                    projectIcon === id 
                      ? 'bg-[#4A9CC7] text-white ring-2 ring-offset-2 ring-[#4A9CC7] dark:ring-offset-slate-800' 
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="dark:text-gray-300">Color</Label>
            <div className="flex gap-2 flex-wrap">
              {PROJECT_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => setProjectColor(color)}
                  className={`w-8 h-8 rounded-lg transition-all ${
                    projectColor === color ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-slate-800 scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          
          {/* Move to Workspace */}
          <div className="border-t dark:border-slate-700 pt-4 mt-4 space-y-3">
            <Label className="text-sm font-medium mb-2 block dark:text-gray-300">
              <ArrowRightLeft className="w-4 h-4 inline mr-2" />
              Move to Workspace
            </Label>
            {loadingWorkspaces ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading workspaces...</span>
              </div>
            ) : availableWorkspaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No other workspaces available. You need member access to at least one other workspace.
              </p>
            ) : (
              <div className="flex gap-2">
                <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                  <SelectTrigger className="flex-1 dark:bg-slate-900 dark:border-slate-600" data-testid="move-workspace-select">
                    <SelectValue placeholder="Select workspace" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {availableWorkspaces.map(ws => (
                      <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                        <div className="flex items-center gap-2">
                          <FolderKanban className="w-4 h-4" style={{ color: ws.color || '#4A9CC7' }} />
                          <span>{ws.name}</span>
                          <span className="text-xs text-muted-foreground">({ws.user_role})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMoveProject}
                  disabled={!selectedWorkspace || movingProject}
                  className="dark:border-slate-600 dark:text-gray-300"
                  data-testid="move-project-btn"
                >
                  {movingProject ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Move'
                  )}
                </Button>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="border-t dark:border-slate-700 pt-4 mt-4 space-y-2">
            <Label className="text-sm font-medium mb-3 block dark:text-gray-300">Actions</Label>
            <Button
              variant="outline"
              size="sm"
              className="mr-2 dark:border-slate-600 dark:text-gray-300"
              onClick={onDuplicate}
            >
              <Copy className="w-4 h-4 mr-2" />
              Duplicate Project
            </Button>
          </div>
          
          {/* Danger Zone */}
          <div className="border-t dark:border-slate-700 pt-4 mt-4">
            <Label className="text-sm font-medium text-red-600 dark:text-red-400 mb-3 block">Danger Zone</Label>
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
              onClick={onDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Project
            </Button>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="dark:border-slate-600 dark:text-gray-300">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={onSave}
            disabled={loading || !projectName.trim()}
            className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
            data-testid="save-project-btn"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSettingsDialog;
